<script setup lang="ts">
import { computed, ref } from "vue";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import { useExcursionOccurrenceDates } from "@/components/RegistrationProcess/composables/useExcursionOccurrenceDates.ts";
import ExcursionImages from "@/components/RegistrationProcess/personalize/Excursion/ExcursionImages/index.vue";
import type { EventWithLocations } from "@/components/RegistrationProcess/types/events.ts";
import { getEventDisplayLocation } from "@/components/RegistrationProcess/utils/eventDisplayLocation";
import { getEventDuration } from "@/components/RegistrationProcess/utils/eventDuration";
import CalendarIcon from "@/components/svg-icons/CalendarIcon.vue";
import SearchChevron from "@/components/svg-icons/SearchChevron.vue";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import ExperienceDetailModal from "./ExperienceDetailModal.vue";
import type { ExperienceCardProps } from "./types";

defineOptions({
  name: "ExperienceCard",
});

const props = withDefaults(defineProps<ExperienceCardProps>(), {
  priceLabel: "",
});

// The storefront color stays on the "Learn More" CTA and its chevron. The subcategory band, the
// price pill and all card copy follow the flag-off convention: gray accent surfaces for branded
// tenants, navy text throughout. Unbranded API tenants are unchanged.
const { secondaryColor, secondaryColorClass } = useTenantTheming();

const svgSecondaryColor = computed<string>(() =>
  secondaryColor.value && secondaryColor.value.length > 0 ? secondaryColor.value : "#007F80",
);

const priceTextClass = computed<string>(() =>
  secondaryColor.value ? "text-university-primary" : "text-university-secondary",
);

const areDetailsVisible = ref<boolean>(false);

const event = computed<EventWithLocations>(() => props.excursion?.event ?? ({} as EventWithLocations));

const eventTitle = computed<string>(() => event.value?.title ?? "");

const subcategory = computed<string>(() => event.value?.event_subcategory?.name ?? "");

const location = computed<string>(() =>
  getEventDisplayLocation(event.value)
    .split(",")
    .slice(0, 2)
    .map((item) => item.trim())
    .join(", "),
);

const duration = computed<string>(() => getEventDuration(event.value));

const description = computed<Record<string, unknown> | undefined>(() => event.value?.description ?? undefined);

const { formattedDateRange, showOccurrenceDates } = useExcursionOccurrenceDates(() => props.excursion, undefined);

const handleLearnMoreClick = (): void => {
  areDetailsVisible.value = true;
};
</script>

<template>
  <ExperienceDetailModal
    v-model:visible="areDetailsVisible"
    :excursion="excursion"
    :price-label="priceLabel"
  />
  <div class="px-2 pb-2 w-full h-full" data-testid="experience-card">
    <div class="flex flex-col h-full w-full bg-white shadow-md overflow-hidden">
      <!-- Subcategory band -->
      <div
        class="flex w-full py-3 px-4 justify-start items-center"
        :class="secondaryColor ? 'bg-gray-275' : 'bg-blue-400'"
      >
        <span
          class="text-sm font-bold uppercase line-clamp-1 text-university-primary"
          data-testid="experience-card-subcategory"
        >
          {{ subcategory }}
        </span>
      </div>

      <!-- Image(s). `data-experience-card-media` is the anchor ExperiencesCarouselSection measures
           to line the carousel arrows up with the bottom of the image. -->
      <ExcursionImages
        data-experience-card-media
        :model-value="event.image ?? ''"
        :image-alt="eventTitle"
        :show-navigators="false"
      />

      <!-- Body -->
      <div class="flex flex-col grow gap-2 p-4 text-university-primary">
        <div v-if="priceLabel" class="flex">
          <div class="p-1 leading-none" :class="secondaryColor ? 'bg-gray-275' : 'bg-teal-400/20'">
            <span
              class="uppercase font-bold text-sm leading-none"
              :class="priceTextClass"
              data-testid="experience-card-price"
            >
              {{ priceLabel }}
            </span>
          </div>
        </div>

        <span class="font-bold text-xl leading-tight line-clamp-3" data-testid="experience-card-title">
          {{ eventTitle }}
        </span>

        <div class="flex flex-col text-sm">
          <span v-if="location" data-testid="experience-card-location">{{ location }}</span>
          <span v-if="duration" data-testid="experience-card-duration">{{ duration }}</span>
        </div>

        <div v-if="showOccurrenceDates" class="flex items-center gap-2 text-sm font-semibold">
          <CalendarIcon class="w-4 h-4" :style="{ color: svgSecondaryColor }" />
          <span data-testid="experience-card-occurrence-dates">{{ formattedDateRange }}</span>
        </div>

        <div v-if="description" class="text-sm" data-testid="experience-card-description">
          <JSONtoHTML class="text-base text-university-primary" :tiptap-json="description" custom-class="line-clamp-3" />
        </div>

        <div class="grow"></div>

        <!-- CTA -->
        <div
          class="uppercase font-bold flex justify-end items-center p-2 gap-[0.625rem] cursor-pointer"
          :class="secondaryColorClass"
          data-testid="experience-card-learn-more"
          @click="handleLearnMoreClick()"
        >
          <span>Learn More</span>
          <div>
            <SearchChevron rotateDirection="right" :fillColor="svgSecondaryColor" :width="14" :height="9" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
